import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import {
  Award, Trophy, Calendar, FileText, Loader2, CheckCircle,
  Star, User, Shield, ExternalLink, Download, Share2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function PublicPortfolioPage() {
  const { affiliationNumber } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadPortfolio();
  }, [affiliationNumber]);

  const loadPortfolio = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_URL}/api/recognition/portfolio/${affiliationNumber}`
      );
      setData(response.data);
    } catch (err) {
      setError('Portafolio no encontrado');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('es-MX', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const contentTypeNames = {
    curso: 'Curso',
    webinar: 'Webinar',
    masterclass: 'Master Class',
    diplomado: 'Diplomado',
    taller: 'Taller',
    ponencia: 'Ponencia',
    platica: 'Plática'
  };

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    alert('Enlace copiado al portapapeles');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-amber-500 mx-auto mb-4" />
          <p className="text-gray-400">Cargando portafolio...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center">
          <User className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Portafolio no encontrado</h1>
          <p className="text-gray-600 mb-6">
            No se encontró un portafolio con el número de afiliación <strong>{affiliationNumber}</strong>
          </p>
          <Link
            to="/"
            className="inline-block px-6 py-3 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700"
          >
            Ir al inicio
          </Link>
        </div>
      </div>
    );
  }

  const { member, constancias, recognitions, stats } = data;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-amber-500/10 to-purple-500/10" />
        <div className="relative max-w-4xl mx-auto px-4 py-16 text-center">
          {/* UPIM Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/20 text-amber-400 rounded-full text-sm font-medium mb-6">
            <Shield className="w-4 h-4" />
            Miembro Verificado UPIM
          </div>

          {/* Member Info */}
          <div className="mb-8">
            <div className="w-24 h-24 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full mx-auto mb-4 flex items-center justify-center text-white text-3xl font-bold shadow-xl">
              {member.name?.charAt(0) || 'U'}
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
              {member.name}
            </h1>
            <p className="text-amber-400 font-mono text-lg tracking-wider mb-2">
              {member.affiliation_number}
            </p>
            {member.membership_status && (
              <span className="inline-block px-3 py-1 bg-green-500/20 text-green-400 text-sm rounded-full">
                Membresía activa
              </span>
            )}
          </div>

          {/* Stats */}
          <div className="flex justify-center gap-8">
            <div className="text-center">
              <p className="text-3xl font-bold text-white">{stats.total_constancias}</p>
              <p className="text-gray-400 text-sm">Constancias</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-amber-400">{stats.total_recognitions}</p>
              <p className="text-gray-400 text-sm">Reconocimientos</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-green-400">{stats.exams_passed}</p>
              <p className="text-gray-400 text-sm">Exámenes</p>
            </div>
          </div>

          {/* Share Button */}
          <button
            onClick={handleShare}
            className="mt-8 inline-flex items-center gap-2 px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors"
          >
            <Share2 className="w-4 h-4" />
            Compartir portafolio
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 pb-16">
        {/* Recognitions */}
        {recognitions && recognitions.length > 0 && (
          <div className="mb-12">
            <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
              <Trophy className="w-5 h-5 text-amber-400" />
              Reconocimientos Obtenidos
            </h2>
            <div className="grid gap-4">
              {recognitions.map(recognition => (
                <div
                  key={recognition.id}
                  className="bg-gradient-to-r from-amber-500/10 to-yellow-500/10 border border-amber-500/30 rounded-xl p-6"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-gradient-to-br from-amber-400 to-amber-600 rounded-xl flex items-center justify-center shadow-lg">
                      <Trophy className="w-7 h-7 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-white">{recognition.program_name}</h3>
                      <p className="text-amber-400 text-sm">{recognition.recognition_title}</p>
                      <div className="flex items-center gap-4 mt-1 text-sm text-gray-400">
                        <span>{recognition.total_constancias} constancias</span>
                        <span>{formatDate(recognition.issued_at)}</span>
                      </div>
                    </div>
                    <CheckCircle className="w-6 h-6 text-green-400" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Constancias */}
        <div>
          <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
            <FileText className="w-5 h-5 text-indigo-400" />
            Constancias ({constancias?.length || 0})
          </h2>
          
          {constancias && constancias.length > 0 ? (
            <div className="space-y-3">
              {constancias.map(constancia => (
                <div
                  key={constancia.id}
                  className="bg-white/5 border border-white/10 rounded-xl p-4 hover:bg-white/10 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="px-2 py-0.5 bg-indigo-500/20 text-indigo-400 text-xs font-medium rounded">
                          {contentTypeNames[constancia.content_type] || constancia.content_type}
                        </span>
                        {constancia.quiz_passed && (
                          <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs font-medium rounded flex items-center gap-1">
                            <CheckCircle className="w-3 h-3" />
                            Examen
                          </span>
                        )}
                      </div>
                      <h3 className="text-white font-medium">{constancia.content_title}</h3>
                      <div className="flex items-center gap-4 mt-1 text-sm text-gray-400">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(constancia.content_date)}
                        </span>
                        {constancia.speaker_name && (
                          <span>{constancia.speaker_name}</span>
                        )}
                      </div>
                    </div>
                    <Link
                      to={`/verificar-constancia/${constancia.verification_code}`}
                      className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg"
                      title="Verificar"
                    >
                      <ExternalLink className="w-5 h-5" />
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white/5 border border-white/10 rounded-xl p-8 text-center">
              <FileText className="w-12 h-12 text-gray-600 mx-auto mb-3" />
              <p className="text-gray-400">Este miembro aún no tiene constancias</p>
            </div>
          )}
        </div>

        {/* Verification Note */}
        <div className="mt-12 text-center">
          <p className="text-gray-500 text-sm">
            Todas las constancias y reconocimientos son verificables en el sistema oficial de UPIM
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-amber-400 hover:text-amber-300 mt-4"
          >
            <ExternalLink className="w-4 h-4" />
            Visitar UPIM Academia
          </Link>
        </div>
      </div>
    </div>
  );
}
